/**
 * The vocabulary {@link ViewBar} speaks — filter/group/sort state that carries no
 * knowledge of what is being listed. Each feature declares its own field unions
 * (see `features/agent-sessions/components/session-controls`) and instantiates these.
 */

export type FilterOperator = 'is' | 'is_not';

export interface ViewFilter<F extends string = string> {
  id: string;
  field: F;
  operator: FilterOperator;
  /** Selected value keys. An empty list is treated as "no constraint". */
  values: string[];
}

export interface FieldOption {
  value: string;
  label: string;
  color: string;
  /** How many rows in the current set carry this value. */
  count: number;
}

/** A selectable field, as listed in the Filter / Group by / Sort by menus. */
export interface FieldDef<F extends string> {
  field: F;
  label: string;
}

export type SortDir = 'asc' | 'desc';

export interface SortSpec<S extends string = string> {
  field: S;
  dir: SortDir;
}

export interface FilterSummary {
  field: string;
  operator: string;
  value: string;
}

export function summarizeFilter<F extends string>(
  filter: ViewFilter<F>,
  options: FieldOption[],
  fields: FieldDef<F>[],
): FilterSummary {
  const field = fields.find((f) => f.field === filter.field)?.label ?? filter.field;
  const operator = filter.operator === 'is' ? (filter.values.length > 1 ? 'is any of' : 'is') : 'is not';
  let value: string;
  if (filter.values.length === 0) {
    value = 'any';
  } else if (filter.values.length === 1) {
    const [only] = filter.values;
    value = options.find((o) => o.value === only)?.label ?? only ?? 'any';
  } else {
    value = `${filter.values.length} values`;
  }
  return { field, operator, value };
}

let filterSeq = 0;

/**
 * Toggle a single value for a field — creating the field's filter on first pick
 * and dropping it when its last value is cleared. One filter per field
 * (Linear-style).
 */
export function toggleFilterValueIn<F extends string>(
  filters: ViewFilter<F>[],
  field: F,
  value: string,
): ViewFilter<F>[] {
  const existing = filters.find((f) => f.field === field);
  if (!existing) return [...filters, { id: `f${++filterSeq}`, field, operator: 'is', values: [value] }];
  const values = existing.values.includes(value)
    ? existing.values.filter((v) => v !== value)
    : [...existing.values, value];
  if (values.length === 0) return filters.filter((f) => f.id !== existing.id);
  return filters.map((f) => (f.id === existing.id ? { ...f, values } : f));
}

/** Keep rows matching every active filter (filters with no selected values are ignored). */
export function applyFilters<T, F extends string>(
  rows: T[],
  filters: ViewFilter<F>[],
  valuesOfField: (row: T, field: F) => string[],
): T[] {
  const active = filters.filter((f) => f.values.length > 0);
  if (active.length === 0) return rows;
  return rows.filter((row) =>
    active.every((filter) => {
      const values = valuesOfField(row, filter.field);
      const hit = filter.values.some((v) => values.includes(v));
      return filter.operator === 'is' ? hit : !hit;
    }),
  );
}
